import { useState, useEffect } from 'react';
import { BusinesscaseRevenue } from '@/types/businesscase';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Info } from 'lucide-react';
import { ExcelImportExport } from './ExcelImportExport';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface RevenuesTableProps {
  revenues: BusinesscaseRevenue[];
  onUpdateRevenue: (revenueId: string, data: Partial<BusinesscaseRevenue>) => void;
  onAddYear: (year: number) => void;
  businesscaseId: string;
  onRefetch?: () => Promise<void>;
  onZymplerClick: () => void;
}

interface SmartInputProps {
  value: number;
  onChange: (value: string) => void;
  className?: string;
}

function SmartInput({ value, onChange, className }: SmartInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState('');

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value.toString());
    }
  }, [value, isFocused]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Als waarde 0 is, maak veld leeg
    if (value === 0) {
      setLocalValue('');
    } else {
      setLocalValue(value.toString());
    }
    e.target.select(); // Selecteer alles voor makkelijk overschrijven
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Als leeg, sla 0 op
    if (localValue === '' || localValue === '-') {
      onChange('0');
      setLocalValue('0');
    } else {
      onChange(localValue);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
  };

  return (
    <Input
      type="number"
      value={isFocused ? localValue : value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cn(
        "h-8 text-sm",
        value === 0 && !isFocused && "text-muted-foreground",
        className
      )}
    />
  );
}

export function RevenuesTable({ revenues, onUpdateRevenue, onAddYear, businesscaseId, onRefetch, onZymplerClick }: RevenuesTableProps) {
  const { toast } = useToast();

  const handleCellChange = (revenue: BusinesscaseRevenue, field: keyof BusinesscaseRevenue, value: string) => {
    const numValue = parseFloat(value) || 0;
    
    // Validate that EMS Costs and Total OPEX should be negative
    if ((field === 'ems_costs' || field === 'total_opex') && numValue > 0) {
      toast({
        title: 'Let op: Positieve waarde',
        description: `${field === 'ems_costs' ? 'EMS Costs' : 'Total OPEX'} moet normaal een negatief getal zijn (bijv. -500)`,
        variant: 'default',
        duration: 3000
      });
    }
    
    // Geen automatische berekening meer - gebruiker kan Total Solar direct aanpassen
    onUpdateRevenue(revenue.id, {
      [field]: numValue
    });
  };

  const handleAddYear = () => {
    const maxYear = Math.max(...revenues.map(r => r.year));
    onAddYear(maxYear + 1);
  };

  const handleImport = async (importedData: Partial<BusinesscaseRevenue>[]) => {
    try {
      // Update existing revenues in database
      for (const data of importedData) {
        if (data.id) {
          const { error } = await supabase
            .from('businesscase_revenues')
            .update(data)
            .eq('id', data.id);

          if (error) throw error;
        }
      }

      // Refetch data instead of page reload
      if (onRefetch) {
        await onRefetch();
      }

      toast({
        title: 'Import geslaagd',
        description: `${importedData.length} jaren succesvol geïmporteerd`
      });
    } catch (error: any) {
      console.error('Error importing data:', error);
      toast({
        title: 'Fout bij importeren',
        description: error.message,
        variant: 'destructive'
      });
      throw error;
    }
  };

  const calculateRevenueSum = (revenue: BusinesscaseRevenue): number => {
    return (
      (revenue.grid_connection_savings || 0) +
      (revenue.grid_purchase_cost_savings || 0) +
      (revenue.dayahead_incomes || 0) +
      (revenue.hbe_incomes || 0) +
      (revenue.energy_tax_savings || 0) +
      (revenue.endex_cost_savings || 0) +
      (revenue.imbalance_income || 0) +
      (revenue.imbalance_discount || 0) +
      (revenue.ems_costs || 0) +
      (revenue.total_opex || 0) +
      (revenue.generator_cost_savings || 0) +
      (revenue.extra_sde || 0)
    );
  };

  const columns = [
    { key: 'grid_connection_savings', label: 'Grid Connection' },
    { key: 'grid_purchase_cost_savings', label: 'Grid Purchase' },
    { key: 'dayahead_incomes', label: 'Dayahead' },
    { key: 'hbe_incomes', label: "HBE's" },
    { key: 'energy_tax_savings', label: 'Energy Tax' },
    { key: 'endex_cost_savings', label: 'Endex' },
    { key: 'imbalance_income', label: 'Imbalance Income' },
    { key: 'imbalance_discount', label: 'Imbalance Discount' },
    { key: 'ems_costs', label: 'EMS Costs (-)' },
    { key: 'total_opex', label: 'Total OPEX (-)' },
    { key: 'generator_cost_savings', label: 'Generator' },
    { key: 'extra_sde', label: 'Extra SDE' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold">Jaarlijkse Opbrengsten</h3>
          <p className="text-sm text-muted-foreground">Vul de verwachte opbrengsten per jaar in (€)</p>
        </div>
        <div className="flex gap-2">
          <ExcelImportExport 
            revenues={revenues} 
            onImport={handleImport}
            onZymplerClick={onZymplerClick}
          />
          <Button onClick={handleAddYear} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Jaar Toevoegen
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-[1600px] w-full border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-2 font-semibold bg-muted sticky left-0 z-10">Jaar</th>
              {columns.map(col => (
                <th key={col.key} className="text-left p-2 font-semibold text-xs">{col.label}</th>
              ))}
              <th className="text-left p-2 font-semibold bg-muted/50 text-xs">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1">
                      Som
                      <Info className="w-3 h-3" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Berekende som van alle revenue streams</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
              <th className="text-left p-2 font-semibold text-xs">Total Solar Revenues</th>
              <th className="text-left p-2 font-semibold bg-primary/10 text-xs">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1">
                      Totaal
                      <Info className="w-3 h-3" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Som + Total Solar Revenues</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
            </tr>
          </thead>
          <tbody>
            {revenues.map((revenue) => {
              const revenueSum = calculateRevenueSum(revenue);
              const grandTotal = revenueSum + (revenue.total_solar_revenues || 0);
              
              return (
                <tr key={revenue.id} className="border-b border-border hover:bg-muted/50">
                  <td className="p-2 font-bold bg-muted sticky left-0">{revenue.year}</td>
                  
                  {/* Invulbare kolommen */}
                  {columns.map(col => (
                    <td key={col.key} className="p-2">
                      <SmartInput
                        value={revenue[col.key as keyof BusinesscaseRevenue] as number}
                        onChange={(value) => handleCellChange(revenue, col.key as keyof BusinesscaseRevenue, value)}
                      />
                    </td>
                  ))}
                  
                  {/* Som kolom (read-only) */}
                  <td className="p-2 bg-muted/30">
                    <div className="h-8 flex items-center font-mono text-sm font-semibold">
                      {revenueSum.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </td>
                  
                  {/* Total Solar Revenues (invulbaar) */}
                  <td className="p-2">
                    <SmartInput
                      value={revenue.total_solar_revenues || 0}
                      onChange={(value) => handleCellChange(revenue, 'total_solar_revenues', value)}
                    />
                  </td>
                  
                  {/* Totaal kolom (read-only) */}
                  <td className="p-2 bg-primary/10">
                    <div className="h-8 flex items-center font-mono text-sm font-bold text-primary">
                      {grandTotal.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
